/**
 * `CompetenceLevelDetail` — pagina di dettaglio di una competenza per la sezione
 * "Livelli", raggiungibile dalla vista "Livelli → Competenze" cliccando il nome
 * di una riga.
 *
 * Mostra:
 *  - i tre box in testa (nome, descrizione, mission) con la **stessa resa** della
 *    competenza selezionata nella pagina di assessment (`AssessmentWizard`);
 *  - una tabella delle persone che coprono la competenza almeno in parte, con
 *    Copertura e Proficiency di ciascuna (colonna "Persona" con nome + reparto +
 *    link alla sua dashboard, come nella vista "Team").
 *
 * Dati: `GET /api/competences/:id/detail` → `CompetenceDetailDto` (rotta
 * protetta lato API da `requireLevelsAccess`, coerentemente con la sezione
 * "Livelli"). La query è abilitata solo quando l'`:competenceId` è presente.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import type { CompetenceDetailDto, CompetenceHolderDto } from '../shared/dto';
import { scoreColor } from '../components/StarRating';
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Icon,
  Loading,
  PageHeader,
  type Column,
  type SortDirection,
} from '../components/ui';

/** Placeholder per un valore non disponibile. */
const PLACEHOLDER = '—';

/**
 * Formatta una percentuale `0..100` come intero con `%`; `null` → placeholder.
 * I valori oltre il 100% (bonus certificazioni) sono mostrati come "100%+",
 * coerentemente col trattamento oro del resto del software.
 */
function formatPercent(value: number | null): string {
  if (value === null) return PLACEHOLDER;
  return value > 100 ? '100%+' : `${Math.round(value)}%`;
}

/**
 * Barra di percentuale (`0..100`) colorata con la scala rosso→verde dei voti
 * (`scoreColor`, percentuale riportata su `0..10`). Affianca il valore numerico.
 * Stessa resa della vista "Livelli → Competenze/Team". I valori oltre il 100%
 * (bonus certificazioni) riempiono la barra al massimo e assumono il trattamento
 * oro con riflesso.
 */
function PercentBar({ value, label }: { value: number; label: string }) {
  const isOverMax = value > 100;
  const width = Math.min(100, Math.max(0, Math.round(value)));
  const exact = Math.round(Math.max(0, value));
  const color = scoreColor(value / 10) ?? undefined;
  return (
    <span
      className="coverage-bar"
      role="progressbar"
      aria-valuenow={exact}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={isOverMax ? `${label} oltre 100% (${exact}%)` : `${label} ${width}%`}
    >
      <span
        className={[
          'coverage-bar__fill',
          isOverMax ? 'coverage-bar__fill--gold' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          width: `${width}%`,
          ...(!isOverMax && color ? { background: color } : {}),
        }}
      />
    </span>
  );
}

/** Comparatore per le colonne percentuali, con i `null` sempre in coda. */
function comparePercent(
  accessor: (row: CompetenceHolderDto) => number | null,
) {
  return (a: CompetenceHolderDto, b: CompetenceHolderDto, direction: SortDirection): number => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    const sign = direction === 'desc' ? -1 : 1;
    return sign * (av - bv);
  };
}

export default function CompetenceLevelDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const { competenceId } = useParams<{ competenceId: string }>();

  const { data, isLoading, isError, error } = useQuery<CompetenceDetailDto>({
    queryKey: ['competences', 'detail', competenceId],
    queryFn: () =>
      api.get<CompetenceDetailDto>(
        `/api/competences/${encodeURIComponent(competenceId ?? '')}/detail`,
      ),
    enabled: competenceId !== undefined && competenceId !== '',
  });

  const backToLevels = () => {
    void navigate('/insights?view=competences');
  };

  const columns = useMemo<Column<CompetenceHolderDto>[]>(
    () => [
      {
        key: 'displayName',
        header: 'Persona',
        sortable: true,
        sortAccessor: (row) => row.displayName.toLowerCase(),
        render: (row) => (
          <div className="coverage-name">
            <Link
              to={`/team/${encodeURIComponent(row.userId)}`}
              className="coverage-name__link"
              aria-label={`Apri la dashboard di ${row.displayName}`}
            >
              {row.displayName}
              <Icon name="external" size={14} className="coverage-name__link-icon" />
            </Link>
            {row.departmentName !== null && (
              <Badge tone="neutral">{row.departmentName}</Badge>
            )}
          </div>
        ),
      },
      {
        key: 'coverage',
        header: 'Copertura',
        sortable: true,
        sortComparator: comparePercent((row) => row.coveragePercent),
        render: (row) => (
          <div className="coverage-cell">
            <PercentBar value={row.coveragePercent} label="Copertura" />
            <span className="coverage-cell__count">
              {formatPercent(row.coveragePercent)}
            </span>
          </div>
        ),
      },
      {
        key: 'proficiency',
        header: 'Proficiency',
        sortable: true,
        sortComparator: comparePercent((row) => row.proficiencyPercent),
        render: (row) =>
          row.proficiencyPercent === null ? (
            <span className="coverage-cell__count">{PLACEHOLDER}</span>
          ) : (
            <div className="coverage-cell">
              <PercentBar value={row.proficiencyPercent} label="Proficiency" />
              <span
                className={[
                  'coverage-cell__count',
                  row.proficiencyPercent > 100 ? 'coverage-cell__count--gold' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {formatPercent(row.proficiencyPercent)}
              </span>
            </div>
          ),
      },
    ],
    [],
  );

  if (isLoading) {
    return <Loading>Caricamento competenza…</Loading>;
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error ? error.message : 'Impossibile caricare la competenza.';
    return (
      <section className="competence-detail" aria-labelledby="competence-level-detail-title">
        <PageHeader
          title="Dettaglio competenza"
          titleId="competence-level-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToLevels}>
              ← Torna alle competenze
            </Button>
          }
        />
        <Alert tone="error">{message}</Alert>
      </section>
    );
  }

  return (
    <section className="competence-detail" aria-labelledby="competence-level-detail-title">
      <PageHeader
        title="Dettaglio competenza"
        titleId="competence-level-detail-title"
        subtitle="Chi la copre in azienda, con Copertura e Proficiency di ciascuno."
        actions={
          <Button type="button" variant="ghost" onClick={backToLevels}>
            ← Torna alle competenze
          </Button>
        }
      />

      {/* Box del nome: stessa resa della competenza selezionata nel wizard. */}
      <div className="competence-picker__selected">
        <div>
          <span className="competence-picker__selected-label">Competenza</span>
          <h2 className="competence-picker__selected-name">{data.name}</h2>
        </div>
      </div>

      {/* Descrizione e mission affiancate, sotto la card del nome. Ogni card
          compare solo se il rispettivo campo è valorizzato — identico al wizard. */}
      {(data.description || data.mission) && (
        <div className="competence-detail__info">
          {data.description && (
            <Card className="competence-detail__info-card">
              <h3 className="competence-detail__info-title">Descrizione</h3>
              <p className="competence-detail__info-text">{data.description}</p>
            </Card>
          )}
          {data.mission && (
            <Card className="competence-detail__info-card">
              <h3 className="competence-detail__info-title">Mission</h3>
              <p className="competence-detail__info-text">{data.mission}</p>
            </Card>
          )}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={data.holders}
        rowKey={(row) => row.userId}
        defaultSort={{ key: 'proficiency', direction: 'desc' }}
        emptyMessage="Nessuna persona copre ancora questa competenza."
        pageSize={15}
      />
    </section>
  );
}
